import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosError,
} from 'axios';
import { StorageKeys, AuthPaths } from './AuthConfig';

/**
 * Token refresh function - set by AuthApiProvider.
 * This refreshes the access token via cookie exchange (not using a refresh token).
 */
let tokenRefresher: (() => Promise<boolean>) | null = null;
let hasRedirectedToLogin = false;

/**
 * Configure the API with token refresh capability.
 * Called by AuthApiProvider when auth is ready.
 * @param refreshAccessToken Function that refreshes the access token via cookie exchange
 */
export function configureApi(refreshAccessToken: () => Promise<boolean>): void {
  tokenRefresher = refreshAccessToken;
  hasRedirectedToLogin = false;
}

/**
 * Get the current access token from localStorage.
 */
export function getAccessToken(): string | null {
  return localStorage.getItem(StorageKeys.AccessToken);
}

/**
 * Check if the stored token is valid (not expired).
 */
export function isTokenValid(): boolean {
  const expiry = localStorage.getItem(StorageKeys.TokenExpiry);
  if (!expiry) return false;
  return Date.now() < parseInt(expiry, 10);
}

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30000,
  // Don't send cookies for API requests - we use JWT Bearer tokens
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - adds auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Response interceptor - handles 401 errors
api.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Check if token expired header is set
      const tokenExpired = error.response.headers['token-expired'] === 'true';

      // Try to refresh token if we have a refresher
      if (tokenRefresher && !hasRedirectedToLogin) {
        console.log('Token expired, attempting refresh...');
        const refreshed = await tokenRefresher();

        if (refreshed && error.config) {
          // Retry the original request with new token
          const newToken = getAccessToken();
          if (newToken && error.config.headers) {
            error.config.headers.Authorization = `Bearer ${newToken}`;
          }
          return axios.request(error.config);
        }
      }

      // Refresh failed or no refresher - redirect to login
      if (!hasRedirectedToLogin) {
        hasRedirectedToLogin = true;
        console.warn('Authentication failed, redirecting to login');
        const returnUrl = encodeURIComponent(
          window.location.pathname + window.location.search
        );
        window.location.href = `${AuthPaths.Login}?returnUrl=${returnUrl}`;
      }
    }
    return Promise.reject(error);
  }
);

export default api;
